import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import type { UncodedDisclosure, UncodedReason } from '../../types/gotypes'
import type { LabelQuery } from '../../components/social-media/types'

export interface UseSkyUncodedDisclosureResult {
  /** The counts, or undefined while they are unknown. */
  disclosure?: UncodedDisclosure
  /** How many comments in this scope are NOT in the charts. */
  notAnalysed: number
  /** 🔴 The distinction useSkyCommentCount's doc comment was written about: a
   *  count that has not arrived is not a count of zero. The button reading
   *  `notAnalysed` hides itself at 0, so a not-yet-loaded state rendered as 0
   *  would hide the disclosure on every first paint and then pop it in. */
  isLoaded: boolean
  isLoading: boolean
  /** The counts are the previous filter's answer while the new one loads. */
  isUpdating: boolean
}

/**
 * How many of this scope's comments are missing from its charts, and why.
 *
 * FETCHED EAGERLY WITH THE DASHBOARD, NOT ON MODAL OPEN. The button that opens
 * the modal displays the count and is hidden entirely when it is zero, so the
 * number has to exist before anyone clicks anything. Deferring the request
 * until open would mean the button could never be drawn.
 *
 * It carries the page's full label query for the same reason every other
 * widget does: this number describes the scope the user is looking at, and a
 * disclosure computed over a different population than the charts would be a
 * new way of being wrong about numbers rather than a fix for the old one. The
 * product's campaigns and (for a fixed department) its source pin are added
 * server-side by skyProductScope, so nothing here names either.
 */
export function useSkyUncodedDisclosure(
  product: string,
  labelQuery?: LabelQuery,
  enabled = true,
): UseSkyUncodedDisclosureResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'uncoded-disclosure', { labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && account.user && enabled),
    retry: 1,
    retryDelay: 1000,
    placeholderData: keepWhileOnlyFilterChanges<UncodedDisclosure>(queryKey),
    queryFn: async ({ signal }): Promise<UncodedDisclosure> => {
      const res = await axios.post<UncodedDisclosure>(
        skyProductURL(product, '/records/uncoded-summary'),
        { labelQuery },
        { signal },
      )
      return res.data
    },
  })

  return {
    disclosure: query.data,
    notAnalysed: countNotAnalysed(query.data),
    isLoaded: query.data !== undefined,
    isLoading: query.isLoading,
    isUpdating: query.isPlaceholderData,
  }
}

/**
 * The single number the button shows: everything we can account for that is not
 * in the charts.
 *
 * 🔴 IT SUMS THE TWO EXCLUSIONS AND NOTHING ELSE. It is emphatically not
 * "imported minus coded" — the never-batched population is deliberately
 * unreported (see UncodedDisclosure), so that subtraction would put a number on
 * screen the modal cannot then explain, which is the exact failure this whole
 * feature exists to end.
 */
export function countNotAnalysed(d?: UncodedDisclosure): number {
  if (!d) return 0
  return sumReasons(d.notCodeable) + sumReasons(d.failed)
}

function sumReasons(reasons?: UncodedReason[]): number {
  if (!reasons) return 0
  return reasons.reduce((total, r) => total + (r.count ?? 0), 0)
}

export default useSkyUncodedDisclosure
